import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {useFlags} from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import spacetime from 'spacetime';

import {
  CREATE_FLOWS,
  CREATE_STEPS,
  DOCUMENT_TYPES,
  INGESTION_STATUSES,
  DOCUMENT_INGESTION_TYPES,
} from '../../../constants';
import {
  useCreateDocumentMutation,
  useCreateUploadFileUrlMutation,
  useUpdateDocumentMutation,
  useCreatePdfReviewDocumentMutation,
} from '../../../data/documentWorkspaceApi';
import {selectEffectiveOrg} from '../../../data/session';
import {
  DARK_TEXT_COLOR,
  GREY_80,
  GREY_BORDER_DEFAULT,
  GREY_TEXT_SECONDARY,
  WHITE_100,
} from '../../../theme';
import {trackProductEvent} from '../../../util';
import {AutoLinkingConfig} from './_AutoLinkingConfig';
import {CancelButton} from './_CancelButton';
import {CompletedView} from './_CompletedView';
import {CTAButton} from './_CTAButton';
import {DocumentNameField} from './_DocumentNameField';
import {ImportFileDropzone} from './_ImportFileDropzone';
import {ModalHeader} from './_ModalHeader';
import {PeriodSelector} from './_PeriodSelector';
import {
  PresentationFormatSelect,
  PriorPresentationFormatSelect,
} from './_PresentationFormatSelect';
import {PreviousDocumentList} from './_PreviousDocumentList';
import {ProcessingView} from './_ProcessingView';
import {ReportTypeSelector} from './_ReportTypeSelector';
import {SourceReportTypeSelect} from './_SourceReportTypeSelect';
import {WorkbookGenerationSelector} from './_WorkbookGenerationSelector';
import {YtdToggle} from './_YtdToggle';
import {YEARS} from './CreateDocumentModal.constants';
import {
  calculateReportPeriod,
  extractYearFromPeriod,
  getAllowedReportTypes,
  getDocumentName,
  getFolderNameForType,
  isRollforwardFlow,
  isUploadFlow,
} from './CreateDocumentModal.utils';

// Co-located styles for this component (used only here)
const localStyles = {
  boldLabel: {
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '700',
    lineHeight: '140%',
  },
  divider: {
    borderBottomWidth: '0.0625rem',
    color: GREY_80,
    opacity: 1,
  },
  modalBase: {
    backgroundColor: WHITE_100,
    borderRadius: '0.5rem',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '680px',
    overflow: 'hidden',
    width: '35rem',
  },
  note: {
    color: GREY_TEXT_SECONDARY,
    fontSize: '0.75rem',
    fontWeight: '400',
    letterSpacing: '0.0075em',
    lineHeight: '140%',
  },
};

export const CreateDocumentModal = ({
  createFlow,
  existingDocumentId,
  onClose,
  onDocumentCreated,
  onError,
  onRollForwardComplete,
  onUploadComplete,
  open,
  previousDocumentId,
  previousDocuments = [],
  sourceDocumentType,
}) => {
  const navigate = useNavigate();
  const {orgConfig} = useSelector((state) => state.session);
  const effectiveOrg = useSelector(selectEffectiveOrg);
  const {selectedFolder} = useSelector((state) => state.folders);
  const {folders} = useSelector((state) => state.folders);
  const [foldersOptions, setFoldersOptions] = useState(null);
  const [assignToFolder, setAssignToFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES.annualReport);
  const [reportPeriod, setReportPeriod] = useState('');
  const [previousPresentationFormat, setPreviousPresentationFormat] =
    useState('');
  const [
    previousPresentationFormatTouched,
    setPreviousPresentationFormatTouched,
  ] = useState(false);
  const [presentationFormat, setPresentationFormat] = useState('');
  const [presentationFormatTouched, setPresentationFormatTouched] =
    useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [documentName, setDocumentName] = useState(
    getDocumentName(
      documentType,
      _.get(orgConfig, 'companyLegalName', orgConfig?.name),
      reportPeriod
    )
  );
  const [documentNameIsDirty, setDocumentNameIsDirty] = useState(false);
  const [step, setStep] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [createDocument, {reset}] = useCreateDocumentMutation();
  const [createPdfReviewDocument, {reset: resetPdfReview}] =
    useCreatePdfReviewDocumentMutation();
  const [createUploadFileUrl] = useCreateUploadFileUrlMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [documentId, setDocumentId] = useState(existingDocumentId);
  const [previousDocument, setPreviousDocument] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isIngestionComplete, setIsIngestionComplete] = useState(false);
  const [years, setYears] = useState(YEARS);
  const [hasDropError, setHasDropError] = useState(null);
  const [ingestionStatus, setIngestionStatus] = useState(
    INGESTION_STATUSES.notStarted
  );
  const [ingestionError, setIngestionError] = useState(null);
  const [rollForwardFolder, setRollForwardFolder] = useState(null);
  const [workbookGeneration, setWorkbookGeneration] = useState('no');
  const [sourceReportType, setSourceReportType] = useState(
    sourceDocumentType || DOCUMENT_TYPES.annualReport
  );
  const [affordableHousingEnabled, setAffordableHousingEnabled] =
    useState(false);
  const [autoLinkingServiceCode, setAutoLinkingServiceCode] = useState('');
  const [includesYtd, setIncludesYtd] = useState(true);
  const [completedDocument, setCompletedDocument] = useState(null);
  const location = useLocation();
  const isDocumentsPage = location.pathname.includes('/documents');
  const {
    autolinkingRfUi: autoLinkingEnabled,
    rollForwardWalkThru: rollForwardWalkThruEnabled,
  } = useFlags();

  const _getPreviousDocument = () => {
    if (createFlow === CREATE_FLOWS.rollforward) {
      const _existingDocument = _.find(previousDocuments, {
        documentId: existingDocumentId,
      });
      if (!_.isNil(_existingDocument)) {
        return _.find(previousDocuments, {
          documentId: _existingDocument?.ingestionState?.previousDocumentId,
        });
      } else if (!_.isNil(previousDocumentId)) {
        return _.find(previousDocuments, {documentId: previousDocumentId});
      }
      return null;
    }

    return null;
  };

  const _buildYearsList = (previousFiscalYear) => {
    let _years = YEARS;
    if (!_.some(_years, (_year) => _year.value === previousFiscalYear)) {
      _years = _.sortBy(
        [
          ..._years,
          {
            label: `FY ${previousFiscalYear}`,
            value: previousFiscalYear,
          },
        ],
        'value'
      );
    }
    return _years;
  };

  useEffect(() => {
    if (open && !isOpen) {
      // Only run reset when opening for first time
      const _quarter = spacetime.now().format('quarter');
      const _previousDocument = _getPreviousDocument();
      let _fiscalYear = spacetime.now().format('year');
      if (!_.isNil(_previousDocument)) {
        _fiscalYear = `${Number(_previousDocument.fiscalYear) + 1}`;
      }
      // Set document type based on source document type when rolling forward.
      // Semi-Annual source defaults to Annual (the only valid transition).
      let initialDocumentType;
      if (createFlow === CREATE_FLOWS.rollforward && sourceDocumentType) {
        const resolvedType =
          sourceDocumentType === DOCUMENT_TYPES.semiAnnualReport
            ? DOCUMENT_TYPES.annualReport
            : sourceDocumentType;
        initialDocumentType = resolvedType;
        setDocumentType(resolvedType);
      } else {
        initialDocumentType = DOCUMENT_TYPES.annualReport;
        setDocumentType(DOCUMENT_TYPES.annualReport);
      }

      const initialReportPeriod = calculateReportPeriod(
        initialDocumentType,
        _fiscalYear,
        _previousDocument?.quarter || _quarter,
        selectedFolder?.year,
        createFlow
      );

      setPreviousPresentationFormat(
        _previousDocument?.presentationFormat ?? ''
      );
      setPreviousPresentationFormatTouched(false);
      setPresentationFormat('');
      setPresentationFormatTouched(false);
      setReportPeriod(initialReportPeriod);
      setDocumentName(
        getDocumentName(
          documentType,
          _.get(orgConfig, 'companyLegalName', orgConfig?.name),
          _fiscalYear,
          _quarter
        )
      );
      setDocumentNameIsDirty(false);
      setStep(
        _.includes(
          [CREATE_FLOWS.rollforward, CREATE_FLOWS.pdf, CREATE_FLOWS.upload],
          createFlow
        )
          ? _.isNil(existingDocumentId)
            ? CREATE_STEPS.create
            : CREATE_STEPS.processing
          : null
      );
      setDocumentId(existingDocumentId);
      setPreviousDocument(_previousDocument);
      setYears(_buildYearsList(_fiscalYear));
      setIsIngestionComplete(false);
      setCompletedDocument(null);
      // setIngestionStatus(INGESTION_STATUSES.notStarted);
      // setIngestionError(null);
      setIsOpen(true);
      setPdfFile(null);
      setLoading(false);
      setWorkbookGeneration(
        orgConfig?.skipWorkbookGeneration === false ? 'yes' : 'no'
      );
      setSourceReportType(sourceDocumentType || DOCUMENT_TYPES.annualReport);
      setAffordableHousingEnabled(false);
      setAutoLinkingServiceCode('');
      setIncludesYtd(true);
    } else if (!open && isOpen) {
      // Reset all state when closing
      setIsOpen(false);
      setIsIngestionComplete(false);
      setCompletedDocument(null);
      setIngestionStatus(INGESTION_STATUSES.notStarted);
      setIngestionError(null);
      setPdfFile(null);
      setStep(null);
      setIsProcessing(false);
    }
  }, [createFlow, existingDocumentId, open, orgConfig, isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      (createFlow === CREATE_FLOWS.pdf || createFlow === CREATE_FLOWS.upload)
    ) {
      const allowedTypes = getAllowedReportTypes(sourceReportType);
      if (!allowedTypes.includes(documentType)) {
        setDocumentType(allowedTypes[0]);
      }
    }
  }, [sourceReportType, isOpen, createFlow]);

  useEffect(() => {
    if (
      _.includes(
        [CREATE_FLOWS.rollforward, CREATE_FLOWS.pdf, CREATE_FLOWS.upload],
        createFlow
      ) &&
      step === CREATE_STEPS.processing
    ) {
      setIsProcessing(true);
    } else {
      setIsProcessing(false);
    }
  }, [step, createFlow]);

  useEffect(() => {
    // Update report period when document type changes
    if (isOpen) {
      // Reset presentation format to avoid stale invalid selections
      setPresentationFormat('');
      setPresentationFormatTouched(false);

      // Reset YTD for quarterly
      setIncludesYtd(true);

      if (isRollforwardFlow(createFlow) || createFlow === CREATE_FLOWS.pdf) {
        const baseYear =
          previousDocument?.fiscalYear || spacetime.now().format('year');
        const baseQuarter =
          previousDocument?.quarter || spacetime.now().format('quarter');

        const newPeriod = calculateReportPeriod(
          documentType,
          baseYear,
          baseQuarter,
          isDocumentsPage ? selectedFolder?.year : null,
          createFlow
        );
        setReportPeriod(newPeriod);
      } else {
        const newPeriod = calculateReportPeriod(
          documentType,
          spacetime.now().format('year'),
          spacetime.now().format('quarter'),
          isDocumentsPage ? selectedFolder?.year : null,
          createFlow
        );
        setReportPeriod(newPeriod);
      }
    }
  }, [
    documentType,
    isOpen,
    createFlow,
    previousDocument,
    isDocumentsPage,
    selectedFolder?.year,
  ]);

  useEffect(() => {
    if (orgConfig && !documentNameIsDirty) {
      setDocumentName(
        getDocumentName(
          documentType,
          documentType === DOCUMENT_TYPES.other
            ? ''
            : _.get(orgConfig, 'companyLegalName', orgConfig.name),
          reportPeriod
        )
      );
    }
  }, [orgConfig, documentType, reportPeriod, documentNameIsDirty]);

  useEffect(() => {
    if (folders && reportPeriod && !isDocumentsPage) {
      const filterYear = extractYearFromPeriod(reportPeriod, documentType);

      const options = folders?.find((folder) => folder[0] === filterYear)?.[1];
      setFoldersOptions(options || []);

      if (isDocumentsPage) {
        setAssignToFolder(selectedFolder?.id);
      } else if (options && options.length > 0) {
        setAssignToFolder(options[0]?.id);
      } else {
        setAssignToFolder(null);
      }
    }
  }, [folders, reportPeriod, selectedFolder?.id, isDocumentsPage]);

  const _getIngestionErrorMessage = () => {
    if (ingestionError?.errorType === 'NO_NAMED_ITEMS') {
      let message = `"${ingestionError.workbookName}" has missing or out of sync links.`;
      message +=
        " Please open the workbook, click 'Sync All' and try again.\n\n";
      message +=
        'If the issue persists, please contact our support team for assistance.';
      return message;
    }

    let message =
      'We encountered a technical issue while trying to roll forward';
    if (ingestionError?.workbookName) {
      message += ` of the workbook "${ingestionError.workbookName}"`;
    }
    message += '. Please contact our support team for assistance.';
    return message;
  };

  const _handleChange = (field) => (event) => {
    const _value = event.target.value;
    switch (field) {
      case 'reportPeriod':
        setReportPeriod(_value);
        if (!documentNameIsDirty) {
          setDocumentName(
            getDocumentName(
              documentType,
              _.get(orgConfig, 'companyLegalName', orgConfig.name),
              _value
            )
          );
        }
        break;
      case 'previousDocument': {
        const _previousDocument = _.find(previousDocuments, {
          documentId: _value,
        });
        setPreviousDocument(_previousDocument);
        if (!_.isNil(_previousDocument)) {
          const allowedTypes = getAllowedReportTypes(_previousDocument.type);
          const newType = allowedTypes[0];
          setDocumentType(newType);

          const _fiscalYear = `${Number(_previousDocument.fiscalYear) + 1}`;
          const newReportPeriod = calculateReportPeriod(
            newType,
            _fiscalYear,
            _previousDocument.quarter
          );

          setReportPeriod(newReportPeriod);
          setYears(_buildYearsList(_fiscalYear));

          if (_previousDocument.presentationFormat) {
            setPreviousPresentationFormat(_previousDocument.presentationFormat);
          }
        }
        break;
      }
      default:
        setDocumentName(_value);
        setDocumentNameIsDirty(true);
    }
  };

  const _createDocument = async () => {
    if (!isDocumentsPage && !assignToFolder) {
      onError(
        '*No available folders for this report period. Please create a folder first.'
      );
      return;
    }

    setLoading(true);
    try {
      let _quarter = null;
      let _fiscalYear = null;
      if (documentType === DOCUMENT_TYPES.quarterlyReport) {
        [_quarter, _fiscalYear] = reportPeriod.split(' ');
      } else {
        _fiscalYear = reportPeriod;
      }

      const _payload = {
        fiscalYear: _fiscalYear,
        folderId: isDocumentsPage ? selectedFolder?.id : assignToFolder,
        name: documentName,
        presentationFormat,
        quarter: _quarter,
        status: 'DRAFT',
        type: documentType,
      };

      let fileName;

      if (createFlow === CREATE_FLOWS.rollforward) {
        _payload.status = 'PENDING';
        _payload.ingestionState = {
          previousDocumentId: _.get(previousDocument, 'documentId'),
          previousDocumentType: previousDocument?.type,
          previousPresentationFormat,
          source: 'DOCUMENT',
          status: 'NOT_STARTED',
          type: 'rollforward',
        };
        if (documentType === DOCUMENT_TYPES.quarterlyReport) {
          _payload.ingestionState.includesYtd = includesYtd;
        }
      } else if (createFlow === CREATE_FLOWS.pdf) {
        fileName = `${crypto.randomUUID()}.${pdfFile?.name?.split('.').pop()}`;
        const rollForwardSourceDocumentKey = `org=${effectiveOrg?.orgId}/file_uploads/${fileName}`;

        _payload.status = 'PENDING';
        _payload.ingestionState = {
          previousDocumentType: sourceReportType,
          previousPresentationFormat,
          skipWorkbookGeneration: workbookGeneration === 'no',
          source: 'PDF',
          sourceDocumentKey: rollForwardSourceDocumentKey,
          status: 'PENDING',
          type: 'rollforward',
        };

        if (documentType === DOCUMENT_TYPES.quarterlyReport) {
          _payload.ingestionState.includesYtd = includesYtd;
        }

        if (affordableHousingEnabled && autoLinkingServiceCode) {
          _payload.ingestionState.autoLinking = {
            enabled: true,
            serviceCode: autoLinkingServiceCode,
          };
        }
      } else if (createFlow === CREATE_FLOWS.upload) {
        fileName = `${crypto.randomUUID()}.${pdfFile?.name?.split('.').pop()}`;
        const pdfS3Key = `org=${orgConfig.org}/file_uploads/${fileName}`;
        _payload.status = 'PENDING';
        _payload.ingestionState = {
          skipWorkbookGeneration: workbookGeneration === 'no',
          source: 'PDF',
          sourceDocumentKey: pdfS3Key,
          status: 'PENDING',
          type: 'upload',
        };
      } else if (createFlow === CREATE_FLOWS.pdfReviewAssistant) {
        const folderId = isDocumentsPage ? selectedFolder?.id : assignToFolder;
        const pdfReviewPayload = {
          file: pdfFile,
          name: pdfFile?.name || 'Untitled PDF',
          type: 'OTHER',
          status: 'PENDING',
          folderId,
          ingestionState: {
            type: DOCUMENT_INGESTION_TYPES.pdfReview,
            source: 'PDF',
            status: 'PENDING',
          },
          fiscalYear: _fiscalYear,
          presentationFormat: 'SINGLE_YEAR',
        };

        try {
          const _pdfReviewDocument =
            await createPdfReviewDocument(pdfReviewPayload);

          if (!_.has(_pdfReviewDocument, 'error')) {
            trackProductEvent('Document Created - PDF Review Assistant', {
              document_id: _.get(_pdfReviewDocument, 'data.documentId'),
              document_type: documentType,
              fiscal_year: _fiscalYear,
              file_size: pdfFile?.size,
              entry_point: 'create_button',
            });

            if (onDocumentCreated) {
              onDocumentCreated();
            }

            const newDocumentId = _.get(_pdfReviewDocument, 'data.documentId');
            if (newDocumentId) {
              navigate(`/documents/pdf-review/${newDocumentId}`);
            }
            _onClose();
          } else {
            console.error('Error creating PDF review document', {
              error: _pdfReviewDocument.error,
            });
            if (
              _.get(_pdfReviewDocument.error, 'data.code') === 'DOCUMENT_EXISTS'
            ) {
              onError(
                'A document with this name already exists. Please choose another name.'
              );
            } else if (
              _.get(_pdfReviewDocument.error, 'data.code') ===
              'INVALID_FOLDER_PURPOSE'
            ) {
              onError('Please select a folder designated for PDF review.');
            } else {
              onError(
                'There was a problem creating your document. Please try again.'
              );
            }
            resetPdfReview();
          }
        } catch (error) {
          console.error('Error creating PDF review document', {error});
          onError(
            'There was a problem creating your document. Please try again.'
          );
          resetPdfReview();
        } finally {
          setLoading(false);
        }
        return;
      } else {
        _payload.createDefaultSections = true;
      }

      const _document = await createDocument(_.omitBy(_payload, _.isNil));
      if (!_.has(_document, 'error')) {
        if (createFlow === CREATE_FLOWS.pdf) {
          trackProductEvent('Document Created - Rollforward', {
            auto_linking_enabled: affordableHousingEnabled,
            auto_linking_service_code: autoLinkingServiceCode || null,
            document_id: _.get(_document.data, 'documentId'),
            document_type: documentType,
            entry_point: 'create_button',
            file_size: pdfFile.size,
            fiscal_year: _fiscalYear,
            presentation_format: presentationFormat,
            quarter: _quarter,
            rollforward_source: 'PDF',
            skip_workbook_generation: workbookGeneration === 'no',
          });

          const folderName = getFolderNameForType(documentType);
          const folderYear = extractYearFromPeriod(reportPeriod, documentType);
          if (selectedFolder?.id !== _.get(_document, 'data.folderId')) {
            setRollForwardFolder({
              id: _.get(_document, 'data.folderId'),
              name: folderName,
              year: folderYear,
            });
          }
          setDocumentId(_.get(_document.data, 'documentId'));
          if (onDocumentCreated) {
            onDocumentCreated();
          }
          const _uploadFileUrl = await createUploadFileUrl({
            documentId: _.get(_document.data, 'documentId'),
            fileName,
            originalFileName: pdfFile.name,
            originalFilePath: pdfFile.path,
          });

          if (!_.has(_uploadFileUrl, 'error')) {
            try {
              if (!pdfFile.size) {
                onError(
                  'The selected file appears to be empty (0 bytes). Please try again with a different file.'
                );
                setPdfFile(null);
                return;
              }

              const uploadResponse = await fetch(
                _uploadFileUrl.data.uploadUrl,
                {
                  body: pdfFile,
                  headers: {
                    'Content-Type': pdfFile.type,
                  },
                  method: 'PUT',
                }
              );

              if (!uploadResponse.ok) {
                throw new Error(
                  `S3 upload failed with status ${uploadResponse.status}`
                );
              }

              const _updateResult = await updateDocument({
                data: {
                  ingestionState: {
                    status: 'NOT_STARTED',
                  },
                },
                documentId: _.get(_document.data, 'documentId'),
              });

              if (_.has(_updateResult, 'error')) {
                console.error('Error updating document status after upload', {
                  error: _updateResult.error,
                });
                onError(
                  'PDF uploaded successfully, but failed to start processing. Please contact support.'
                );
                reset();
                return;
              }

              setStep(CREATE_STEPS.processing);
              if (assignToFolder && !isDocumentsPage) {
                navigate(`/folders/${assignToFolder}/documents`);
              }
            } catch (uploadError) {
              console.error('Error uploading PDF', {
                documentId: _.get(_document.data, 'documentId'),
                error: uploadError,
              });
              onError(
                'There was a problem uploading your PDF. Please try again.'
              );
              reset();
            }
          } else {
            console.error('Error generating upload URL', {
              documentId: _.get(_document.data, 'documentId'),
              error: _uploadFileUrl.error,
            });
            onError(
              'There was a problem processing your request. Please try again later.'
            );
            reset();
          }
        } else if (createFlow === CREATE_FLOWS.rollforward) {
          trackProductEvent('Document Created - Rollforward', {
            document_id: _.get(_document.data, 'documentId'),
            document_type: documentType,
            entry_point: previousDocumentId
              ? 'document_context_menu'
              : 'create_button',
            fiscal_year: _fiscalYear,
            presentation_format: presentationFormat,
            previous_document_id: _.get(previousDocument, 'documentId'),
            previous_presentation_format: previousPresentationFormat,
            quarter: _quarter,
            rollforward_source: 'DOCUMENT',
          });

          const folderName = getFolderNameForType(documentType);
          const folderYear = extractYearFromPeriod(reportPeriod, documentType);
          setRollForwardFolder({
            id: _.get(_document, 'data.folderId'),
            name: folderName,
            year: folderYear,
          });
          setDocumentId(_.get(_document, 'data.documentId'));
          if (onDocumentCreated) {
            onDocumentCreated();
          }
          setStep(CREATE_STEPS.processing);
        } else if (createFlow === CREATE_FLOWS.upload) {
          trackProductEvent('Document Created - Upload', {
            document_id: _.get(_document.data, 'documentId'),
            document_type: documentType,
            entry_point: 'create_button',
            file_size: pdfFile.size,
            fiscal_year: _fiscalYear,
            presentation_format: presentationFormat,
            quarter: _quarter,
            skip_workbook_generation: workbookGeneration === 'no',
          });

          const folderName = getFolderNameForType(documentType);
          const folderYear = extractYearFromPeriod(reportPeriod, documentType);
          if (selectedFolder?.id !== _.get(_document, 'data.folderId')) {
            setRollForwardFolder({
              id: _.get(_document, 'data.folderId'),
              name: folderName,
              year: folderYear,
            });
          }

          const newDocumentId = _.get(_document.data, 'documentId');
          setDocumentId(newDocumentId);
          if (onDocumentCreated) {
            onDocumentCreated();
          }

          // Upload the file
          const _uploadFileUrl = await createUploadFileUrl({
            documentId: newDocumentId,
            fileName,
            originalFileName: pdfFile.name,
            originalFilePath: pdfFile.path,
          });

          if (!_.has(_uploadFileUrl, 'error')) {
            try {
              if (!pdfFile.size) {
                onError(
                  'The selected file appears to be empty (0 bytes). Please try again with a different file.'
                );
                setPdfFile(null);
                return;
              }

              const uploadResponse = await fetch(
                _uploadFileUrl.data.uploadUrl,
                {
                  body: pdfFile,
                  headers: {
                    'Content-Type': pdfFile.type,
                  },
                  method: 'PUT',
                }
              );

              if (!uploadResponse.ok) {
                throw new Error(
                  `S3 upload failed with status ${uploadResponse.status}`
                );
              }

              const _updateResult = await updateDocument({
                data: {
                  ingestionState: {
                    status: 'NOT_STARTED',
                  },
                },
                documentId: newDocumentId,
              });

              if (_.has(_updateResult, 'error')) {
                console.error('Error updating document status after upload', {
                  error: _updateResult.error,
                });
                onError(
                  'File uploaded successfully, but failed to start processing. Please contact support.'
                );
                reset();
                return;
              }

              setStep(CREATE_STEPS.processing);
              if (assignToFolder && !isDocumentsPage) {
                navigate(`/folders/${assignToFolder}/documents`);
              }
            } catch (uploadError) {
              console.error('Error uploading file', {
                documentId: newDocumentId,
                error: uploadError,
              });
              onError(
                'There was a problem uploading your file. Please try again.'
              );
              reset();
            }
          } else {
            console.error('Error generating upload URL', {
              documentId: newDocumentId,
              error: _uploadFileUrl.error,
            });
            onError(
              'There was a problem processing your request. Please try again later.'
            );
            reset();
          }
        } else {
          trackProductEvent('Document Created - Blank', {
            document_id: _.get(_document.data, 'documentId'),
            document_type: documentType,
            entry_point: 'create_button',
            fiscal_year: _fiscalYear,
            presentation_format: presentationFormat,
            quarter: _quarter,
          });

          if (onDocumentCreated) {
            onDocumentCreated();
          }
          const newDocumentId = _.get(_document.data, 'documentId');
          if (newDocumentId) {
            navigate(`/documents/${newDocumentId}`);
          }
          _onClose();
        }
      } else {
        console.error('Error creating document', {error: _document.error});
        if (_.get(_document.error, 'data.code') === 'DOCUMENT_EXISTS') {
          onError(
            'Filename exists in this organization. Please choose another.'
          );
        } else {
          onError();
        }
        reset();
      }
    } catch (error) {
      console.error('Error creating document', {error});
      if (_.get(error, 'data.code') === 'DOCUMENT_EXISTS') {
        onError('Filename exists in this organization. Please choose another.');
      } else {
        onError();
      }
      reset();
    } finally {
      setLoading(false);
    }
  };

  const _validateInputs = () => {
    let _hasFilename = true;
    let _hasPreviousDocument = true;
    if (
      (createFlow === CREATE_FLOWS.pdf ||
        createFlow === CREATE_FLOWS.upload ||
        createFlow === CREATE_FLOWS.pdfReviewAssistant) &&
      (_.isNil(pdfFile) || pdfFile.size === 0)
    ) {
      _hasFilename = false;
    }
    if (createFlow === CREATE_FLOWS.rollforward && _.isNil(previousDocument)) {
      _hasPreviousDocument = false;
    }
    if (createFlow === CREATE_FLOWS.blankDocument && !presentationFormat) {
      return false;
    }
    if (createFlow === CREATE_FLOWS.upload && !presentationFormat) {
      return false;
    }
    if (createFlow === CREATE_FLOWS.pdfReviewAssistant) {
      const folderId = isDocumentsPage ? selectedFolder?.id : assignToFolder;
      return _hasFilename && !_.isNil(folderId);
    }
    if (
      createFlow !== CREATE_FLOWS.blankDocument &&
      createFlow !== CREATE_FLOWS.upload &&
      (!presentationFormat || !previousPresentationFormat)
    ) {
      return false;
    }
    // Validate service code is selected when affordable housing is enabled
    if (
      createFlow === CREATE_FLOWS.pdf &&
      affordableHousingEnabled &&
      !autoLinkingServiceCode
    ) {
      return false;
    }
    // Don't validate assignToFolder here - let user click and get helpful toast message

    switch (documentType) {
      case DOCUMENT_TYPES.annualReport:
      case DOCUMENT_TYPES.quarterlyReport:
      case DOCUMENT_TYPES.semiAnnualReport:
        return (
          !_.isEmpty(reportPeriod) &&
          !_.isEmpty(documentName) &&
          _hasFilename &&
          (isUploadFlow(createFlow) || _hasPreviousDocument)
        );
      default:
        return (
          !_.isEmpty(documentName) &&
          _hasFilename &&
          (isUploadFlow(createFlow) || _hasPreviousDocument)
        );
    }
  };

  const _onRollForwardComplete = (document) => {
    setCompletedDocument(document);
    setIsIngestionComplete(true);
    setIsProcessing(false);
    setIngestionStatus(INGESTION_STATUSES.completed);
    onRollForwardComplete();
  };

  const _onUploadComplete = (document) => {
    setCompletedDocument(document);
    setIsIngestionComplete(true);
    setIsProcessing(false);
    setIngestionStatus(INGESTION_STATUSES.completed);
    if (onUploadComplete) {
      onUploadComplete();
    }
    if (onDocumentCreated) {
      onDocumentCreated();
    }
  };

  const _handleOpenReport = () => {
    if (
      completedDocument?.ingestionState?.type === 'rollforward' &&
      completedDocument?.ingestionState?.showWalkThrough &&
      completedDocument?.ingestionState?.source !== 'PDF' &&
      rollForwardWalkThruEnabled
    ) {
      navigate(`/rollforward-walk-through/${documentId}`, {
        state: {document: completedDocument},
      });
    } else {
      navigate(`/documents/${documentId}`);
    }
  };

  const _onClose = () => {
    setHasDropError(false);
    setIngestionStatus(INGESTION_STATUSES.notStarted);
    setIngestionError(null);
    setIsIngestionComplete(false);
    setCompletedDocument(null);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const isPdfReviewAssistantFlow =
    createFlow === CREATE_FLOWS.pdfReviewAssistant;

  return (
    <Modal onClose={_onClose} open={open}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box
          component="form"
          sx={localStyles.modalBase}
          onSubmit={(e) => {
            e.preventDefault();
            if (_validateInputs() && !loading && !isProcessing) {
              _createDocument();
            }
          }}
        >
          {/* Completed View (shown after processing) */}
          <CompletedView
            isIngestionComplete={isIngestionComplete}
            onOpenReport={_handleOpenReport}
          />
          {/* Header */}
          <ModalHeader
            createFlow={createFlow}
            isIngestionComplete={isIngestionComplete}
            isProcessing={isProcessing}
            onClose={_onClose}
          />

          {/* Scrollable Content */}
          {!isIngestionComplete && (
            <Box sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
              <ImportFileDropzone
                createFlow={createFlow}
                hasDropError={hasDropError}
                isProcessing={isProcessing}
                onError={onError}
                pdfFile={pdfFile}
                renderPriorPresentationFormat={() => (
                  <PriorPresentationFormatSelect
                    previousDocument={previousDocument}
                    previousPresentationFormat={previousPresentationFormat}
                    previousPresentationFormatTouched={
                      previousPresentationFormatTouched
                    }
                    setPreviousPresentationFormat={
                      setPreviousPresentationFormat
                    }
                    setPreviousPresentationFormatTouched={
                      setPreviousPresentationFormatTouched
                    }
                  />
                )}
                renderSourceReportType={() => (
                  <SourceReportTypeSelect
                    setSourceReportType={setSourceReportType}
                    sourceReportType={sourceReportType}
                  />
                )}
                setHasDropError={setHasDropError}
                setPdfFile={setPdfFile}
              />
              <PreviousDocumentList
                createFlow={createFlow}
                isProcessing={isProcessing}
                onPreviousDocumentChange={_handleChange('previousDocument')}
                previousDocument={previousDocument}
                previousDocuments={previousDocuments}
                previousPresentationFormat={previousPresentationFormat}
                previousPresentationFormatTouched={
                  previousPresentationFormatTouched
                }
                setPreviousPresentationFormat={setPreviousPresentationFormat}
                setPreviousPresentationFormatTouched={
                  setPreviousPresentationFormatTouched
                }
              />
              <Divider orientation={'horizontal'} sx={localStyles.divider} />
              {!isPdfReviewAssistantFlow && (
                <Stack
                  direction={'column'}
                  paddingBottom={'1.5rem'}
                  paddingTop={'1.25rem'}
                  paddingX={'1.5rem'}
                  spacing={'1.25rem'}
                >
                  {/* Create Form */}
                  {!isProcessing && !isIngestionComplete && (
                    <>
                      <Stack
                        alignItems={'center'}
                        direction={'row'}
                        justifyContent={'space-between'}
                      >
                        <Typography style={localStyles.boldLabel}>
                          New report details
                        </Typography>
                        <Typography style={localStyles.note}>
                          * Required info
                        </Typography>
                      </Stack>
                      <ReportTypeSelector
                        createFlow={createFlow}
                        documentType={documentType}
                        previousDocument={previousDocument}
                        setDocumentType={setDocumentType}
                        sourceDocumentType={
                          createFlow === CREATE_FLOWS.pdf ||
                          createFlow === CREATE_FLOWS.upload
                            ? sourceReportType
                            : sourceDocumentType
                        }
                      />
                      <PresentationFormatSelect
                        createFlow={createFlow}
                        presentationFormat={presentationFormat}
                        presentationFormatTouched={presentationFormatTouched}
                        previousPresentationFormat={previousPresentationFormat}
                        setPresentationFormat={setPresentationFormat}
                        setPresentationFormatTouched={
                          setPresentationFormatTouched
                        }
                      />
                      <YtdToggle
                        createFlow={createFlow}
                        documentType={documentType}
                        includesYtd={includesYtd}
                        setIncludesYtd={setIncludesYtd}
                      />
                      <PeriodSelector
                        assignToFolder={assignToFolder}
                        createFlow={createFlow}
                        documentType={documentType}
                        foldersOptions={foldersOptions}
                        isDocumentsPage={isDocumentsPage}
                        onReportPeriodChange={_handleChange('reportPeriod')}
                        reportPeriod={reportPeriod}
                        selectedFolder={selectedFolder}
                        setAssignToFolder={setAssignToFolder}
                        years={years}
                      />
                      <DocumentNameField
                        documentName={documentName}
                        onDocumentNameChange={_handleChange('documentName')}
                      />
                      <WorkbookGenerationSelector
                        createFlow={createFlow}
                        orgConfig={orgConfig}
                        setWorkbookGeneration={setWorkbookGeneration}
                        workbookGeneration={workbookGeneration}
                      />
                      <AutoLinkingConfig
                        affordableHousingEnabled={affordableHousingEnabled}
                        autoLinkingEnabled={autoLinkingEnabled}
                        autoLinkingServiceCode={autoLinkingServiceCode}
                        createFlow={createFlow}
                        setAffordableHousingEnabled={
                          setAffordableHousingEnabled
                        }
                        setAutoLinkingServiceCode={setAutoLinkingServiceCode}
                      />
                    </>
                  )}
                  {/* Processing View */}
                  {isProcessing && !isIngestionComplete && (
                    <ProcessingView
                      documentId={documentId}
                      documentLabel={
                        createFlow === CREATE_FLOWS.upload
                          ? 'Upload document'
                          : 'Roll forward document'
                      }
                      errorMessage={
                        createFlow !== CREATE_FLOWS.upload
                          ? _getIngestionErrorMessage()
                          : undefined
                      }
                      ingestionError={ingestionError}
                      ingestionStatus={ingestionStatus}
                      onComplete={
                        createFlow === CREATE_FLOWS.upload
                          ? _onUploadComplete
                          : _onRollForwardComplete
                      }
                      rollForwardFolder={rollForwardFolder}
                      setIngestionError={setIngestionError}
                      setIngestionStatus={setIngestionStatus}
                    />
                  )}
                </Stack>
              )}
            </Box>
          )}

          {/* Fixed Footer */}
          {!isIngestionComplete && (
            <Box
              sx={{
                backgroundColor: 'white',
                borderTop: `0.0625rem solid ${GREY_BORDER_DEFAULT}`,
                flexShrink: 0,
              }}
            >
              <Stack
                alignItems={'center'}
                direction={'row'}
                justifyContent={'flex-end'}
                paddingLeft={'1.5rem'}
                paddingRight={'1rem'}
                paddingY={'1rem'}
                spacing={'0.625rem'}
              >
                <CancelButton
                  isIngestionComplete={isIngestionComplete}
                  isProcessing={isProcessing}
                  onClick={_onClose}
                />
                <CTAButton
                  disabled={!_validateInputs()}
                  documentId={documentId}
                  ingestionStatus={ingestionStatus}
                  isIngestionComplete={isIngestionComplete}
                  isProcessing={isProcessing}
                  isPdfReviewAssistantFlow={isPdfReviewAssistantFlow}
                  loading={loading}
                  onClose={_onClose}
                  onCreate={_createDocument}
                />
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

CreateDocumentModal.propTypes = {
  createFlow: PropTypes.string,
  existingDocumentId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDocumentCreated: PropTypes.func,
  onError: PropTypes.func.isRequired,
  onRollForwardComplete: PropTypes.func,
  onUploadComplete: PropTypes.func,
  open: PropTypes.bool.isRequired,
  previousDocumentId: PropTypes.string,
  previousDocuments: PropTypes.array,
  sourceDocumentType: PropTypes.string,
};
